import type { Action } from '@0xtorch/core'
import { createEvmActionSource, isHex } from '@pkg/evm'
import { createSolanaActionSource } from '@pkg/solana'
import { ApiSecretRequiredException } from '../../errors'
import type { FetchApiData } from '../../types'
import {
  type BinanceExchange,
  binanceExchange,
  networkEvmChainIds,
  solanaNetwork,
} from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

/**
 * 取引所ごとの 入金履歴 データ取得関数を作成する
 * @param params.exchange Binance API を利用する取引所の識別情報
 * @returns 入金履歴 データ取得関数
 */
export const createFetchApiData =
  ({
    exchange,
  }: {
    exchange: BinanceExchange
  }): FetchApiData =>
  async ({ apiKey, apiSecret, config, from, createHeaders, proxy, to }) => {
    if (!apiSecret) {
      throw new ApiSecretRequiredException()
    }

    const client = createClient({
      accountCategory: exchange.accountCategory,
      apiKey,
      apiSecret,
      createHeaders,
      proxy,
    })

    const response = await client.run({
      method: 'GET',
      path: '/sapi/v1/capital/deposit/hisrec',
      query: {
        limit: config.limit.toString(),
        ...(from ? { startTime: from.toString() } : {}),
        ...(to ? { endTime: to.toString() } : {}),
      },
      responseSchema,
    })

    // 昇順 sort by insertTime (as specified in README)
    const sortedDeposits = response.sort((a, b) => a.insertTime - b.insertTime)

    // データ処理
    let stopped = false
    let fetchedFrom: number | undefined
    let fetchedTo: number | undefined
    const sources: string[] = []
    const actions: Action[] = []

    for (const item of sortedDeposits) {
      // Skip rejected (status 2) or wrong deposits (status 7)
      if (item.status === 2 || item.status === 7) {
        continue
      }

      // Stop processing if deposit is pending (status 0) or waiting for user confirmation (status 8)
      if (item.status === 0 || item.status === 8) {
        stopped = true
        break
      }

      // Update fetchedFrom and fetchedTo
      fetchedFrom =
        fetchedFrom === undefined
          ? item.insertTime
          : Math.min(fetchedFrom, item.insertTime)
      fetchedTo =
        fetchedTo === undefined
          ? item.insertTime
          : Math.max(fetchedTo, item.insertTime)

      // Create EVM source if applicable
      if (item.network && item.txId) {
        const evmChainId = networkEvmChainIds[item.network]
        if (evmChainId && isHex(item.txId)) {
          sources.push(
            createEvmActionSource({
              chainId: evmChainId,
              transactionHash: item.txId,
            }),
          )
        }
      }

      // Create Solana source if applicable
      if (item.network === solanaNetwork && item.txId) {
        sources.push(createSolanaActionSource({ signature: item.txId }))
      }
    }

    return {
      actions,
      count: response.length,
      from: fetchedFrom,
      sources,
      stopped,
      to: fetchedTo,
    }
  }

/** Binance の 入金履歴 データ取得関数 */
export const fetchApiData = createFetchApiData({ exchange: binanceExchange })
